import ReducerRegistry from "seneca-common/utils/state/reducer-registry";

import "../analytics";
import { PATH_TO_COURSES_STATE } from "./const";
import reducer from "./reducers/state";
import * as contentsSelectors from "./selectors/state/contents";
import * as contentSelectors from "./selectors/state/contents/content";
import * as coursesSelectors from "./selectors/state/courses";
import * as courseSelectors from "./selectors/state/courses/course";
import * as modulesSelectors from "./selectors/state/modules";
import * as moduleSelectors from "./selectors/state/modules/module";
import * as sectionsSelectors from "./selectors/state/sections";
import * as sectionSelectors from "./selectors/state/sections/section";

const reducerRegistry = ReducerRegistry.getInstance();
reducerRegistry.initialise(PATH_TO_COURSES_STATE, builder => {
  builder.register(PATH_TO_COURSES_STATE, reducer);
});

export { reducer, PATH_TO_COURSES_STATE };

// actions
export {
  receiveCoursesDetails,
  addCourse,
  addCourseSkeleton,
  addSections,
  addSection
} from "./actions/course";
export {
  fetchCourseDetailsActionFactory,
  fetchCourseActionFactory,
  fetchCourseSectionsActionFactory,
  fetchSectionActionFactory
} from "./actions/meta";

// selectors
export {
  // courses
  getAllCourseIds,
  getAllCourses,
  getCourse,
  getCourseName,
  getCourseNames,
  getCourseAuthors,
  getCourseReferences,
  getCourseHeaderImageURL,
  courseIsPremium,
  getCourseTagIds,
  getRelatedCourseIds,
  courseExists,
  getCourseStartingSectionId,
  getCourseSectionIds,
  getCourseSubscriptionTier,
  getCourseRegion,
  getCourseExamQuestionsCount,
  doesCourseHaveExamQuestions,
  // sections
  getAllSections,
  getSection,
  getSections,
  getSectionParentId,
  getCourseDescription,
  getSectionTitle,
  getSectionContentIds,
  getSectionSectionIds,
  isLeafSection,
  getSectionExamQuestionsCount,
  doesSectionHaveExamQuestions,
  getAllSectionContentIds, // Use makeGetAllSectionContentIds instead if possible
  getAllSectionContentIdsFromSections, // Use makeGetAllSectionContentIdsFromSections instead if possible
  //contents
  getContent,
  getContentModuleIds,
  contentIsSequential,
  contentModuleIdIsLastInContent,
  getFirstContentModuleId,
  getFollowingContentModuleId,
  includesContentModuleId,
  // modules
  getContentModuleType,
  getContentModuleContent,
  getContentModuleParentId,
  getAllContentModules,
  getContentModuleDifficulty
} from "./selectors/state";
export {
  coursesSelectors,
  courseSelectors,
  sectionsSelectors,
  sectionSelectors,
  contentsSelectors,
  contentSelectors,
  modulesSelectors,
  moduleSelectors
};
export {
  getAllCourseContentIds, // Use makeGetAllCourseContentIds instead if possible
  makeGetAllCourseContentIds
} from "./selectors/getAllCourseContentIds";
export {
  getAllSectionNumbers, // Use makeGetAllSectionNumbers instead if possible
  makeGetAllSectionNumbers
} from "./selectors/getAllSectionNumbers";
export {
  getSectionNumber, // Use makeGetSectionNumber instead if possible
  makeGetSectionNumber
} from "./selectors/getSectionNumber";
export { makeGetSectionTitleWithNumber } from "./selectors/getSectionTitleWithNumber";
export {
  getDescendantLeafSectionIds, // Use makeGetDescendantLeafSectionIds instead if possible
  makeGetDescendantLeafSectionIds
} from "./selectors/getDescendantLeafSectionIds";
export {
  makeGetCourseLeafSectionIds,
  getLeafSectionIds
} from "./selectors/getLeafSectionIds";
export {
  getSectionAndItsParentsNamesWithoutCourse // not memoized
} from "./selectors/getSectionAndItsParents";
export {
  makeGetSectionContentModules,
  makeGetSectionContentModuleIds
} from "./selectors/getSectionContentModules";
export { default as makeGetSectionAnchorConceptModuleIds } from "./selectors/getSectionAnchorConceptModuleIds";
export { makeGetSectionContents } from "./selectors/getSectionContents";
export { default as makeGetSectionHasSequentialContent } from "./selectors/getSectionHasSequentialContent";
export {
  getSectionCourseId,
  makeGetAllSectionContentIds,
  makeGetAllSectionContentIdsFromSections,
  getAllSubSubSectionIds
} from "./selectors/section";
export {
  // course details
  getCourseDetailsFetchStarted,
  getCourseDetailsFetchSuccess,
  getCourseDetailsFetchError,
  courseDetailsNeedFetching,
  courseDetailsFetchFinished,
  // full course
  getFullCourseFetchStarted,
  getFullCourseFetchSuccess,
  getFullCourseFetchError,
  getFullCourseFetchErrorMessage,
  getCourseDoesNotExist,
  getIsInvalidSusbcriptionForCourse,
  fullCourseNeedFetching,
  fullCourseFetchFinished,
  // course sections
  getCourseSectionsFetchStarted,
  getCourseSectionsFetchSuccess,
  getCourseSectionsFetchError,
  courseSectionsFetchFinished,
  courseSectionsNeedFetching,
  getCourseSectionsDoNotExist,
  // section
  getSectionFetchStarted,
  getSectionFetchSuccess,
  getSectionFetchError,
  sectionFetchFinished,
  sectionNeedsFetching,
  getIsInvalidSubscriptionForSection,
  getSectionDoesNotExist
} from "./selectors/meta";

// MODELS
export { default as CoursesStateSlice } from "./models/state/CoursesStateSlice";
// course
export { default as Course } from "./models/state/courses/course/Course";
export type {
  CourseId,
  CourseType,
  References
} from "./models/state/courses/course/Course";
export type { Courses } from "./models/state/courses/Courses";
// section
export { default as Section } from "./models/state/sections/section/Section";
export type {
  SectionId,
  SectionType
} from "./models/state/sections/section/Section";
export type { Sections } from "./models/state/sections/Sections";
// content
export { default as Content } from "./models/state/contents/content/Content";
export type { ContentType } from "./models/state/contents/content/Content";
export { default as ContentOptions } from "./models/state/contents/content/options/ContentOptions";
export type { ContentOptionsType } from "./models/state/contents/content/options/ContentOptions";
export type { Contents } from "./models/state/contents/Contents";
// modules
export { default as ContentModule } from "./models/state/modules/module/ContentModule";
export type {
  ModuleId,
  ContentModuleType
} from "./models/state/modules/module/ContentModule";
export type { Modules } from "./models/state/modules/Modules";
//drafts
export { default as CourseDraft } from "./models/CourseDraft";
export type { CourseDraftType } from "./models/CourseDraft";
export { default as ContentModuleDraft } from "./models/ContentModuleDraft";
export type { ContentModuleDraftType } from "./models/ContentModuleDraft";
